// frontend_MainDashboard/src/components/Auth/SignupPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import Spinner from '../UI/Spinner';
import { BotIcon, CheckCircleIcon, XMarkIcon } from '../UI/Icons';
import { API_BASE_URL } from '../../constants';

interface InvitationDetails {
  email: string;
  workspaceName: string;
  role: string;
  inviterEmail: string;
}

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  
  const { currentUser, signupWithEmail, idToken, isLoading: authLoading, error: authError } = useAuth();
  
  const { acceptInvitation } = useClient();
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  
  // Invitation state
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [isLoadingInvitation, setIsLoadingInvitation] = useState(false);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  
  // Signup state
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [acceptingInvitation, setAcceptingInvitation] = useState(false);
  const [hasAttemptedAccept, setHasAttemptedAccept] = useState(false);

  // Validate invitation token on mount
  useEffect(() => {
    if (inviteToken) {
      validateInvitation();
    }
  }, [inviteToken]);

  // Auto-accept if user is ALREADY logged in (or just finished signup and component remounted)
  useEffect(() => {
    if (currentUser && invitation && inviteToken && !hasAttemptedAccept && !acceptingInvitation && !signupSuccess) {
       // Only if emails match
       if (currentUser.email?.toLowerCase() === invitation.email.toLowerCase()) {
           console.log("Auto-accepting invitation for logged in user");
           handleAcceptInvitation();
       }
    }
  }, [currentUser, invitation, inviteToken, hasAttemptedAccept, acceptingInvitation, signupSuccess]);

  // Handle successful signup - accept invitation
  useEffect(() => {
    if (signupSuccess && inviteToken && idToken && !acceptingInvitation && !hasAttemptedAccept) {
      handleAcceptInvitation();
    }
  }, [signupSuccess, inviteToken, idToken, acceptingInvitation, hasAttemptedAccept]);

  // Redirect authenticated users (without invite) to dashboard
  useEffect(() => {
    // Modify: Only redirect if NO invite OR if logged in but email mismatch (not auto-accepting)
    if (currentUser && !isSigningUp && !signupSuccess) {
       if (!inviteToken) {
           navigate('/', { replace: true });
       } else if (invitation && currentUser.email?.toLowerCase() !== invitation.email.toLowerCase()) {
           // Mismatch - don't redirect yet, let user see form? 
           // actually this case is tricky. Form is disabled if email mismatch.
           // do nothing, let user decide (maybe Link to logout in future)
       }
    }
  }, [currentUser, inviteToken, isSigningUp, signupSuccess, navigate, invitation]);

  const validateInvitation = async () => {
    setIsLoadingInvitation(true);
    setInvitationError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/invitation/validate/${inviteToken}`);
      const data = await response.json();
      
      if (response.ok && data.success) {
        setInvitation(data.invitation);
        setEmail(data.invitation.email); // Pre-fill email from invitation
      } else {
        setInvitationError(data.message || 'Invalid invitation link.');
      }
    } catch (err) {
      console.error('Error validating invitation:', err);
      setInvitationError('Could not validate invitation. Please try again.');
    } finally {
      setIsLoadingInvitation(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!inviteToken || !idToken) return;
    
    setAcceptingInvitation(true);
    setHasAttemptedAccept(true);
    
    try {
      // Use the context method instead of direct fetch
      const result = await acceptInvitation(inviteToken);
      
      if (result.success) {
        // Successfully accepted - redirect to dashboard
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1500);
      } else {
        setLocalError(result.message || 'Could not accept invitation.');
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setLocalError('Could not accept invitation. Please try again.');
    } finally {
      setAcceptingInvitation(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    
    // Validation
    if (!email.trim()) {
      setLocalError('Email is required.');
      return;
    }
    
    if (!password) {
      setLocalError('Password is required.');
      return;
    }
    
    if (password.length < 6) {
      setLocalError('Password must be at least 6 characters.');
      return;
    }
    
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match.');
      return;
    }
    
    // If invitation exists, ensure email matches
    if (invitation && email.toLowerCase() !== invitation.email.toLowerCase()) {
      setLocalError('Please use the email address the invitation was sent to.');
      return;
    }
    
    setIsSigningUp(true);
    
    try {
      await signupWithEmail(email, password, displayName || undefined);
      setSignupSuccess(true);
      
      // If no invitation, redirect to dashboard after short delay
      if (!inviteToken) {
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1500);
      }
    } catch (err: any) {
      // Check for user already exists error
      if (err.code === 'auth/email-already-in-use' || err.message?.includes('email-already-in-use')) { 
        console.log('User already exists, redirecting to login...');
        const redirectUrl = inviteToken ? `/login?invite=${inviteToken}&email=${encodeURIComponent(email)}` : '/login';
        navigate(redirectUrl, { replace: true });
        return;
      }

      // Error is handled by AuthContext
      console.log('Signup error:', err);
    } finally {
      setIsSigningUp(false);
    }
  };

  // Show loading while validating invitation
  if (inviteToken && isLoadingInvitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-200">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  // Show error for invalid invitation
  if (inviteToken && invitationError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-200 py-12 px-4">
        <div className="max-w-md w-full bg-white p-10 rounded-xl shadow-2xl text-center">
          <XMarkIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h2>
          <p className="text-gray-600 mb-6">{invitationError}</p>
          <Link
            to="/login"
            className="inline-block px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  // Show success/processing screen (also for auto-accept case)
  const isAutoAccepting = currentUser && invitation && inviteToken && (acceptingInvitation || hasAttemptedAccept) && currentUser.email?.toLowerCase() === invitation.email.toLowerCase();

  // Show success screen after signup with invitation
  if ((signupSuccess && inviteToken) || isAutoAccepting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-200 py-12 px-4">
        <div className="max-w-md w-full bg-white p-10 rounded-xl shadow-2xl text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome!</h2>
          <p className="text-gray-600 mb-2">
            {signupSuccess ? 'Your account has been created successfully.' : 'You are logged in.'}
          </p>
          {acceptingInvitation ? (
            <div className="flex items-center justify-center mt-4">
              <Spinner size="sm" />
              <span className="ml-2 text-gray-600">Joining workspace...</span>
            </div>
          ) : (
            <p className="text-gray-600">
               {localError ? 'Invitation processing complete.' : 
               <>You now have access to <strong>{invitation?.workspaceName}</strong> as {invitation?.role}.</>}
            </p>
          )}
          {localError && acceptingInvitation === false && (
             <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
                {localError}
                <div className="mt-2">
                   <button onClick={() => navigate('/')} className="text-red-700 underline font-medium">
                      Continue to Dashboard
                   </button>
                </div>
             </div>
          )}
          <p className="text-sm text-gray-500 mt-4">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  // Show success screen for regular signup (no invitation)
  if (signupSuccess && !inviteToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-200 py-12 px-4">
        <div className="max-w-md w-full bg-white p-10 rounded-xl shadow-2xl text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Created!</h2>
          <p className="text-gray-600">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  const displayError = localError || authError;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-200 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white p-10 rounded-xl shadow-2xl">
          {/* Header */}
          <div className="text-center">
            <div className="mx-auto h-16 w-16 text-green-600 flex items-center justify-center">
              <BotIcon className="h-12 w-12" />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              {invitation ? 'Accept Invitation' : 'Create Account'}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {invitation 
                ? `Join ${invitation.workspaceName} as ${invitation.role}`
                : 'Sign up for OpenMirror Dashboard'
              }
            </p>
          </div>

          {/* Invitation Banner */}
          {invitation && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>{invitation.inviterEmail}</strong> has invited you to join{' '}
                <strong>{invitation.workspaceName}</strong> as {invitation.role}.
              </p>
            </div>
          )}

          {/* Signup Form */}
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Display Name (optional) */}
              <div>
                <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-1">
                  Your Name (optional)
                </label>
                <input
                  id="displayName"
                  name="displayName"
                  type="text"
                  autoComplete="name"
                  className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  placeholder="John Doe"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  disabled={!!invitation} // Disable if invited (pre-filled)
                  className={`appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm ${
                    invitation ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            </div>

            {/* Error Display */}
            {displayError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600 text-center">{displayError}</p>
              </div>
            )}

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isSigningUp || authLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSigningUp || authLoading ? (
                  <Spinner size="sm" color="border-white" />
                ) : invitation ? (
                  'Create Account & Join Workspace'
                ) : (
                  'Create Account'
                )}
              </button>
            </div>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-green-600 hover:text-green-500">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
